import { useEffect, useCallback, useRef, useState } from "react";
import { Route, Redirect } from "react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { Container, Loader, Dimmer } from "semantic-ui-react";
import {
  ACCESS_TOKEN,
  BYPASS,
  USE_COOKIE_AUTH,
} from "src/common/Auth/auth0-variables";
import { shouldMockData } from "src/common";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import { setUserInfo } from "src/redux/actions/userInfo";

const getRoles = (user) => {
  const roles = user["https://www.ctmsmart.com/roles"] || [];

  // Condense our roles into a string. String should look like: traveler;clientmanager
  return roles.reduce((acc, curr) => {
    let newStr;

    switch (curr.code) {
      case "TA":
        newStr = "travelarranger";
        break;
      case "CM":
        newStr = "clientmanager";
        break;
      case "T":
        newStr = "traveler";
        break;
      default:
        break;
    }

    if (newStr) {
      return acc.length ? `${acc};${newStr}` : newStr;
    } else return acc;
  }, "");
};

const getFirstandLastName = (user) => {
  const fullNameArray = (user["https://www.ctmsmart.com/fullName"] || "").split(
    " ",
  );
  return [fullNameArray[0] || "", fullNameArray[1] || ""];
};

const setPendo = (user, userToken) => {
  if (!user || !userToken) return;
  const [firstname, lastname] = getFirstandLastName(user);
  const params = {
    visitor: {
      firstname,
      lastname,
      id: user["https://www.ctmsmart.com/email"],
      language: "English",
      email: user["https://www.ctmsmart.com/email"],
      role: getRoles(user),
    },
    account: {
      id: user["https://www.ctmsmart.com/debtor"],
      name: userToken.companyName,
      internal: !import.meta.env.VITE_IS_PROD || Boolean(shouldMockData()),
    },
  };
  // @ts-expect-error
  window?.pendo?.initialize(params);
};

const ProtectedRoute = ({ component: Component, ...rest }) => {
  const dispatch = useDispatch();
  const {
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    user: auth0User,
  } = useAuth0();

  // Cookie auth: call /auth/check then /auth/userinfo on mount.
  const [cookieIsLoading, setCookieIsLoading] = useState(USE_COOKIE_AUTH);
  const [cookieIsAuthenticated, setCookieIsAuthenticated] = useState(false);
  const [cookieUser, setCookieUser] = useState(null);

  useEffect(() => {
    if (!USE_COOKIE_AUTH) return;
    const checkAuth = async () => {
      try {
        const checkRes = await fetch(`/auth/check`);
        if (checkRes.ok) {
          const userInfoRes = await fetch(`/auth/userinfo`);
          if (userInfoRes.ok) {
            const userInfoBody = await userInfoRes.json();
            setCookieUser(userInfoBody);
            setCookieIsAuthenticated(true);
            dispatch(setUserInfo(userInfoBody));
          }
        }
      } catch {
        // Not authenticated — cookieIsAuthenticated stays false.
      } finally {
        setCookieIsLoading(false);
      }
    };
    checkAuth();
  }, [dispatch]);

  const isAuthenticated = USE_COOKIE_AUTH
    ? cookieIsAuthenticated
    : auth0IsAuthenticated;
  const isLoading = USE_COOKIE_AUTH ? cookieIsLoading : auth0IsLoading;
  const user = USE_COOKIE_AUTH ? cookieUser : auth0User;

  // Auth0 / BYPASS paths: feed claims into the userInfo slice so the rest of
  // the app (useUserInfo / getUserInfo) doesn't need to care which auth mode
  // is active.
  useEffect(() => {
    if (USE_COOKIE_AUTH) return;
    if (auth0IsAuthenticated && auth0User) {
      dispatch(setUserInfo(auth0User));
    }
  }, [dispatch, auth0IsAuthenticated, auth0User]);

  const userToken = rest?.userToken;
  const userState = userToken?.state;

  const storedAccessToken = localStorage.getItem(ACCESS_TOKEN);
  const useStoredAccessToken =
    !USE_COOKIE_AUTH &&
    Boolean(localStorage.getItem(BYPASS)) &&
    Boolean(storedAccessToken);
  const isLoggedIn = useStoredAccessToken || isAuthenticated;

  // BYPASS dev path: decode the stored JWT once and populate the userInfo slice.
  useEffect(() => {
    if (!useStoredAccessToken) return;
    try {
      const decoded = jwt_decode(storedAccessToken);
      if (decoded) dispatch(setUserInfo(decoded));
    } catch {
      // Bad token — leave userInfo unset; downstream consumers must handle null.
    }
  }, [dispatch, useStoredAccessToken, storedAccessToken]);

  const asyncScriptsLoaded = useRef(false);

  const setBeamer = useCallback(() => {
    const roleString = getRoles(user);
    const [firstName, lastName] = getFirstandLastName(user);

    // Beamer config needs to be global for the beamer script to access it
    // @ts-ignore
    window.beamer_config = {
      product_id: "iIFQQPdX51905",
      // Allows messages to be filtered by role
      filter: roleString,
      user_email: user["https://www.ctmsmart.com/email"],
      // Allows messages to be filtered by debtor
      user_id:
        user["https://www.ctmsmart.com/elysian/userid"] ||
        user["https://www.ctmsmart.com/email"],
      user_firstname: firstName,
      user_lastname: lastName,
      // 10px off the bottom
      bottom: 10,
      // Offsets the width of the scrollbar
      right: 25,
    };

    const script = document.createElement("script");
    script.src = "https://app.getbeamer.com/js/beamer-embed.js";
    script.defer = true;
    document.head.appendChild(script);
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && userState?.features) {
      if (!asyncScriptsLoaded.current) {
        setPendo(user, userToken);
        setBeamer();
        asyncScriptsLoaded.current = true;
      }
    }
  }, [userState, isAuthenticated]);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isLoading) {
          return (
            <Container fluid style={{ height: "100%" }}>
              <Dimmer active>
                <Loader>Please Wait</Loader>
              </Dimmer>
            </Container>
          );
        }
        return isLoggedIn ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: {
                from: props.location,
              },
            }}
          />
        );
      }}
    />
  );
};

export default ProtectedRoute;
